<template>
    <div v-if="show"
        class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
        <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
            <h3 class="text-xl font-semibold text-primary mb-4">{{ isAdding ? 'Add New' : 'Edit' }} {{ dataType }}</h3>
            <form @submit.prevent="handleSubmit">
                <div v-for="header in orderedHeaders" :key="header" class="mb-4">
                    <label :for="header" class="block text-sm font-medium text-gray-700 mb-1">{{ header }}</label>
                    <template v-if="header === 'moduleCode' && dataType === 'Activity/Staff'">
                        <div class="relative">
                            <input :id="header" v-model="searchQueries[header]" @input="filterOptions(header)"
                                type="text" placeholder="Search module..."
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            <div v-if="showDropdown[header]"
                                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                <div v-for="option in filteredOptions[header]" :key="option.id"
                                    @click="selectOption(header, option)"
                                    class="px-3 py-2 cursor-pointer hover:bg-gray-100">
                                    {{ getOptionLabel(header, option) }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="header === 'activityId' && dataType === 'Activity/Staff'">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                :disabled="!editingItem.moduleCode"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option value="">Select an activity</option>
                                <option v-for="activity in moduleActivities" :key="activity.activityId"
                                    :value="activity.activityId">
                                    {{ activity.activityId }}
                                </option>
                            </select>
                        </div>
                    </template>
                    <template
                        v-else-if="header === 'school' && !['studentModuleActivity', 'activityStaff'].includes(dataType)">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                :disabled="header == 'department' && dataType == 'Activity/Staff' || header == 'school' && dataType == 'Activity/Staff'
                                                || header == 'department' && dataType == 'Student/Module/Activity' || header == 'school' && dataType == 'Student/Module/Activity'"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option v-for="school in schools" :key="school.shortName" :value="school.fullName">
                                    {{ school.fullName }}
                                </option>
                            </select>
                        </div>
                    </template>
                    <template
                        v-else-if="header === 'department' && !['students', 'studentModuleActivity', 'activityStaff'].includes(dataType)">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                :disabled="header == 'department' && dataType == 'Activity/Staff' || header == 'school' && dataType == 'Activity/Staff'
                                || header == 'department' && dataType == 'Student/Module/Activity' || header == 'school' && dataType == 'Student/Module/Activity'"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option v-for="dept in filteredDepartments" :key="dept.shortName"
                                    :value="dept.fullName">
                                    {{ dept.fullName }}
                                </option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="header === 'image' && dataType === 'Staff'">
                        <div class="relative">
                            <input type="file" id="staffImage" accept="image/*" @change="handleImageUpload"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            <img v-if="editingItem.image" :src="editingItem.image" alt="Staff Image Preview"
                                class="mt-2 w-32 h-32 object-cover rounded-md">
                        </div>
                    </template>
                    <template v-else-if="header === 'activityId'">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <option value="">Select an activity</option>
                                <option v-for="activity in moduleActivities" :key="activity.activityId"
                                    :value="activity.activityId">
                                    {{ activity.activityId }}
                                </option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="header === 'isFixed'">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <!--true and false-->
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="header === 'isExcluded'">
                        <div class="relative">
                            <select :id="header" v-model="editingItem[header]"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                                <!--true and false-->
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </template>
                    <template v-else-if="isSearchableField(header)">
                        <div class="relative">
                            <input :id="header" v-model="searchQueries[header]" @input="filterOptions(header)"
                                type="text" :placeholder="`Search ${header}...`" :disabled="!isAdding && header=='studentName' && dataType=='Student/Module/Activity' || !isAdding && header =='activityId' && dataType == 'Activity/Staff'"
                                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
                            <div v-if="showDropdown[header]"
                                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                <div v-for="option in filteredOptions[header]" :key="option.id"
                                    @click="selectOption(header, option)"
                                    class="px-3 py-2 cursor-pointer hover:bg-gray-100">
                                    {{ getOptionLabel(header, option) }}
                                </div>
                            </div>
                        </div>
                    </template>
                    <input v-else :id="header" v-model="editingItem[header]" type="text" 
                        :disabled="header === 'studentId' && dataType === 'Student/Module/Activity' || header == 'staffId' && dataType == 'Activity/Staff' ||
                            header == 'department' && dataType == 'Activity/Staff' || header == 'school' && dataType == 'Activity/Staff'"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        :class="{ 'bg-gray-100': header === 'studentId' && dataType === 'Student/Module/Activity' }">
                </div>
                <div class="flex justify-end space-x-2">
                    <button type="button" @click="$emit('close')"
                        class="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 w-24 h-10 text-sm font-medium">
                        Cancel
                    </button>
                    <button type="submit" 
                        class="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-900 w-24 h-10 text-sm font-medium">
                        {{ isAdding ? 'Add' : 'Save' }}
                    </button>
                </div>
            </form>
        </div>
        <div v-if="loading" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="spinner"></div>
        </div>
    </div>
</template>

<script>
import { dataApi } from '@/api/dataManagement';
export default {
    name: 'EditorModal',
    props: {
        show: Boolean,
        isAdding: Boolean,
        dataType: String,
        editingItem: Object,
        headers: Array,
        schools: Array,
        departments: Array,
        students: Array,
        modules: Array,
        activities: Array,
        staffMembers: Array,
        fetchActivitiesForModule: {
            type: Function,
            required: true
        },
    },
    data() {
        return {
            searchQueries: {},
            showDropdown: {},
            filteredOptions: {},
            moduleActivities: [],
            loading: false
        };
    },
    computed: {
        filteredDepartments() {
            if (!this.editingItem || !this.editingItem.school) return this.departments;
            const schoolShortName = this.schools.find(
                school => school.fullName === this.editingItem.school
            )?.shortName;
            return this.departments.filter(
                dept => dept.school === schoolShortName
            );
        },
        orderedHeaders() {
            if (this.dataType === 'Activity/Staff') {
                return ['moduleCode', 'activityId', 'staffName', ...this.headers.filter(h => !['moduleCode', 'activityId', 'staffName'].includes(h))];
            }
            return this.headers;
        },
    },
    watch: {
        editingItem: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    this.headers.forEach(header => {
                        if (this.isSearchableField(header)) {
                            this.$set(this.searchQueries, header, newValue[header] || '');
                        }
                    });
                    if (this.editingItem.moduleCode)
                        this.fetchActivitiesForSelectedModule(this.editingItem.moduleActivities)
                }
            }
        }
    },
    methods: {
        handleSubmit() {
            if (this.dataType === 'Student/Module/Activity') {
                // We don't need to set school and department here
                // They will be fetched from the modules array when needed
            }
            console.log(this.editingItem)
            
            this.$emit('submit', this.editingItem);
        },
        async handleImageUpload(event) {
            const file = event.target.files[0];
            if (file) {
                /*
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.$set(this.editingItem, 'image', e.target.result);
                };
                reader.readAsDataURL(file);
                */
                try {
                    const result = await dataApi.uploadStaffImage(this.editingItem.id, file);
                    if (result && result.image) {
                        this.$set(this.editingItem, 'image', `data:image/jpeg;base64,${result.image}`);
                    } else {
                        console.error('Image upload failed: No image data received');
                    }
                } catch (error) {
                    console.error('Error uploading image:', error);
                    // Add error handling here, e.g., show an error message to the user
                }
            }
        },
        async fetchStudents(query){
            const result = await dataApi.fetchData('students', 1, 10, { searchQuery: query });
            return result.items;
        },
        isSearchableField(header) {
            return ['studentName', 'moduleCode', 'activityId', 'staffName'].includes(header);
        },
        async filterOptions(header) {
            this.showDropdown[header] = true;
            const query = this.searchQueries[header] ? this.searchQueries[header].toLowerCase() : "";
            let options;
            switch (header) {
                case 'studentName':
                    options = await this.fetchStudents(query);
                    break;
                case 'moduleCode':
                    options = this.modules;
                    break;
                case 'activityId':
                    options = this.activities;
                    break;
                case 'staffName':
                    options = this.staffMembers;
                    break;
                default:
                    options = [];
            }
            this.filteredOptions[header] = options.filter(option =>
                this.getOptionLabel(header, option).toLowerCase().includes(query)
            );
        },
        async fetchActivitiesForSelectedModule(moduleCode) {
            if (!moduleCode) moduleCode = this.editingItem.moduleCode;
            this.loading = true;
            try {
                const filters = {
                    searchQuery: '',
                    school: '',
                    department: '',
                    moduleCode: moduleCode,
                };
                const result = await dataApi.fetchData('activities', 1, 999, filters);
                this.moduleActivities = result.items;
            } catch (error) {
                console.error('Error fetching activities:', error);
            } finally {
                this.loading = false;
            }
        },
        selectOption(header, option) {
            if (header === 'studentName') {
                this.editingItem.studentName = option.name;
                this.editingItem.studentId = option.studentId;
            } else if (header === 'staffName') {
                this.editingItem.staffName = option.name;
                this.editingItem.staffId = option.staffId;
            } else if (header === 'moduleCode') {
                this.editingItem.moduleCode = option.moduleCode;
                this.editingItem.department = this.modules.find(x => x.moduleCode == option.moduleCode)?.department;
                this.editingItem.school = this.modules.find(x => x.moduleCode == option.moduleCode)?.school;
                this.fetchActivitiesForSelectedModule(option.moduleCode);
            } else {
                this.editingItem[header] = this.getOptionLabel(header, option);
            }

            this.searchQueries[header] = this.getOptionLabel(header, option);
            this.showDropdown[header] = false;
        },
        getOptionLabel(header, option) {
            switch (header) {
                case 'studentName':
                    return `${option.name} (${option.studentId})`;
                case 'moduleCode':
                    return `${option.moduleCode} - ${option.name}`;
                case 'activityId':
                    return option.activityId;
                case 'staffName':
                    return `${option.name} (${option.staffId})`;
                default:
                    return '';
            }
        },
    },
    created() {
        this.headers.forEach(header => {
            if (this.isSearchableField(header)) {
                this.$set(this.searchQueries, header, this.editingItem[header] || '');
                this.$set(this.showDropdown, header, false);
                this.$set(this.filteredOptions, header, []);
            }
        });
    },
};
</script>

